import * as Ariakit from '@ariakit/react'
import { useQuery } from '@tanstack/react-query'
import classnames from 'classnames'
import * as React from 'react'

import { getSanitizedTicker } from '../../../app/shared/ticker-sanitizer'
import { searchApiRequest } from '../../services/api'
import { link } from '../../services/routing'
import { Instrument } from '../../types'
import { Button } from '../button'
import { IconNameType } from '../icon'
import { InputSize, InputTheme } from '../input'
import { Search, SearchProps, useSearchComboboxState } from '../search'
import { NoResultsFound } from '../search/no-results-found'
import { TickerSearchItem } from './TickerSearchItem'
import { getRedirectForTicker } from './util'

interface Props extends Omit<SearchProps, 'state'> {
  /**
   * Treat input as multiticker search when sanitizing tickers (eg. Add tickers to portfolio input)
   */
  isMultiTicker?: boolean

  /**
   * Whether the input should automatically receive focus when mounted
   */
  autoFocus?: boolean

  /**
   * Restrict to stocks only or other instruments
   */
  instrument?: Instrument

  /**
   * Any special symbols like "$CASH", which will get ignored when sanitizing tickers
   */
  specialSymbols?: string[]

  /**
   * Force the combobox state when handling error tooltips, etc.
   */
  open?: boolean

  /**
   * Initial value for the input field
   */
  defaultValue?: string

  /**
   * Default placement position of the combobox popover
   * Inherits placement options from Ariakit ComboboxStore
   */
  defaultPlacement?: Ariakit.ComboboxStoreProps['placement']

  /**
   * Placeholder text shown when the input is empty
   */
  placeholder?: string

  /**
   * Visual theme variant for the input
   * References predefined styles from InputTheme
   */
  theme?: keyof typeof InputTheme

  /**
   * Size variant for the input
   * References predefined sizes from InputSize
   */
  size?: keyof typeof InputSize

  /**
   * Additional CSS class name for the input element
   */
  inputClass?: string

  /**
   * Additional CSS class name for the popover
   */
  popoverClass?: string

  /**
   * ChartIndex used for data-testid
   */
  chartIndex?: number

  /**
   * Whether to show the "Show All" option
   *
   * @default true
   */
  isShowAllVisible?: boolean

  /**
   * Whether to show the "No results found" message
   *
   * @default false
   */
  isShowNoResults?: boolean

  /**
   * Whether search items should be rendered as links
   *
   * @default true
   */
  isSearchItemLink?: boolean

  /**
   * Content to show on the right side of the input (icon or custom element)
   */
  rightContent?: React.JSX.Element | IconNameType

  /**
   * Callback function triggered when a ticker is selected
   *
   * @param ticker - The sanitized selected ticker string
   * @param originalValue - The original, unsanitized value
   */
  onTickerSelected?: (ticker: string, originalValue: string) => void

  /**
   * Callback function triggered when user cycles through the options
   *
   * @param ticker - The selected ticker string
   */
  onActiveValueChange?: (ticker: string) => void
}

export function TickerSearch({
  isMultiTicker,
  specialSymbols,
  open,
  isShowAllVisible = true,
  isShowNoResults = false,
  isSearchItemLink = true,
  chartIndex,
  instrument,
  inputComponent: trigger,
  onTickerSelected,
  onActiveValueChange,
  ...props
}: Props) {
  const searchComboboxState = useSearchComboboxState({
    open,
    defaultValue: props.defaultValue,
    placement: props.defaultPlacement ?? 'bottom-start',
    setSelectedValue: (value) => {
      if (isSearchItemLink) {
        window.location.href = getRedirectForTicker(value)
      } else {
        const sanitizedValue = getSanitizedTicker(value, isMultiTicker, specialSymbols)
        if (sanitizedValue.length === 0) return

        onTickerSelected?.(sanitizedValue, value)
        // We have to reset the value (clear the input) in next tick otherwise it doesn't work
        setTimeout(() => searchComboboxState.setValue(''))
        searchComboboxState.hide()
      }
    },
  })

  const value = Ariakit.useStoreState(searchComboboxState, 'value')
  const activeValue = Ariakit.useStoreState(searchComboboxState, 'activeValue')
  const { data, error, isLoading, refetch } = useQuery(['search', value], () => searchApiRequest(value, instrument), {
    enabled: value.length > 0,
    keepPreviousData: true,
    cacheTime: Infinity,
    staleTime: Infinity,
  })

  React.useEffect(() => {
    if (activeValue) onActiveValueChange?.(activeValue)
  }, [activeValue, onActiveValueChange])

  return (
    <Search
      state={searchComboboxState}
      hasData={isShowNoResults ? true : data && data?.length > 0}
      aria-label="Search"
      inputComponent={trigger}
      inputProps={{
        theme: props.theme,
        maxLength: 100,
        hasFocusStyle: !activeValue,
        size: props.size,
        placeholder: props.placeholder ?? 'Search symbol',
        inputClass: classnames(props.inputClass, {
          'focus:border-blue-500 dark:focus:border-blue-400': !!value && !activeValue,
        }),
        rightContent: props.rightContent,
        enterKeyHint: 'go',
      }}
      popoverProps={{
        className: classnames(props.popoverClass, 'h-auto', {
          'w-[--popover-available-width] max-w-110': !props.isMatchInputAndBoxWidth,
        }),
      }}
      inputTestId={chartIndex !== undefined ? `chart-${chartIndex}-settings-search-input` : undefined}
      footer={
        isShowAllVisible ? (
          <Button
            as="a"
            className="w-full shrink-0 self-center"
            contentClass="text-center"
            href={link.toSearch(activeValue ?? value)}
          >
            Show All Results
          </Button>
        ) : undefined
      }
      isLoading={isLoading}
      isError={!!error}
      onErrorRetry={refetch}
      {...props}
    >
      {isShowNoResults && data?.length === 0 ? (
        <NoResultsFound />
      ) : (
        <div className="search-list">
          {data?.map((item) => (
            <TickerSearchItem
              key={item.ticker}
              state={searchComboboxState}
              item={item}
              dataTestId={
                chartIndex !== undefined ? `chart-${chartIndex}-settings-suggestion-${item.ticker}` : undefined
              }
              {...(isSearchItemLink
                ? {
                    as: 'a',
                    href: getRedirectForTicker(item.ticker),
                  }
                : {})}
            />
          ))}
        </div>
      )}
    </Search>
  )
}
